import Cookies from 'js-cookie';

export type CookieCategory = 'essential' | 'functional' | 'analytics' | 'marketing';

export interface CookieConsent {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  lastUpdated: Date;
}

interface AuthCookieData {
  userId: string;
  role: string;
  councilRole?: string;
  timestamp: string;
}

const COOKIE_CONSENT_KEY = 'cookie_consent';
const AUTH_COOKIE_KEY = 'auth_session';
const COOKIE_EXPIRY_DAYS = 365;

function isSecureContext(): boolean {
  return window.location.protocol === 'https:';
}

export const cookieService = {
  getConsent(): CookieConsent | null {
    let raw = Cookies.get(COOKIE_CONSENT_KEY);

    if (!raw) {
      try {
        raw = localStorage.getItem(COOKIE_CONSENT_KEY) || undefined;
      } catch {
        // localStorage unavailable
      }
    }

    if (!raw) return null;

    try {
      const parsedConsent = JSON.parse(raw);
      return {
        ...parsedConsent,
        lastUpdated: new Date(parsedConsent.lastUpdated)
      };
    } catch {
      return null;
    }
  },

  setConsent(consent: Partial<CookieConsent>): CookieConsent {
    const currentConsent = this.getConsent() || {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
      lastUpdated: new Date()
    };

    const newConsent = {
      ...currentConsent,
      ...consent,
      essential: true,
      lastUpdated: new Date()
    };

    const serialized = JSON.stringify(newConsent);

    Cookies.set(COOKIE_CONSENT_KEY, serialized, {
      expires: COOKIE_EXPIRY_DAYS,
      secure: isSecureContext(),
      sameSite: 'strict'
    });

    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, serialized);
    } catch {
      // localStorage unavailable
    }

    return newConsent;
  },

  setAuthCookie(userId: string, data: Omit<AuthCookieData, 'userId' | 'timestamp'>) {
    const cookieData: AuthCookieData = {
      userId,
      ...data,
      timestamp: new Date().toISOString()
    };

    Cookies.set(AUTH_COOKIE_KEY, JSON.stringify(cookieData), {
      expires: COOKIE_EXPIRY_DAYS,
      secure: true,
      sameSite: 'strict',
      path: '/'
    });
  },

  getAuthCookie(): AuthCookieData | null {
    const cookie = Cookies.get(AUTH_COOKIE_KEY);
    if (!cookie) return null;

    try {
      return JSON.parse(cookie);
    } catch {
      return null;
    }
  },

  removeAuthCookie() {
    Cookies.remove(AUTH_COOKIE_KEY, { path: '/' });
  },

  clearConsent() {
    Cookies.remove(COOKIE_CONSENT_KEY);
    try {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
    } catch {
      // localStorage unavailable
    }
  },

  isAllowed(category: CookieCategory): boolean {
    const consent = this.getConsent();
    return consent ? consent[category] : category === 'essential';
  },

  hasConsentExpired(): boolean {
    const consent = this.getConsent();
    if (!consent) return true;

    const lastUpdated = new Date(consent.lastUpdated);
    const now = new Date();
    const daysSinceConsent = (now.getTime() - lastUpdated.getTime()) / (1000 * 60 * 60 * 24);
    
    return daysSinceConsent >= COOKIE_EXPIRY_DAYS;
  }
};